import { StrictMode } from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import './index.css'
import { initI18n, syncHtmlLang, applyLandingLanguage, onLanguagePreferenceChange } from './i18n'
import { isLandingPath, landingLang } from './utils/landing'
import { api } from './services/api'
import App from './App.tsx'
// Efeito colateral: captura o evento `beforeinstallprompt` assim que possível,
// antes do React montar (o navegador o dispara cedo e uma única vez).
import './services/pwaInstall'

const rootEl = document.getElementById('root')!

// Inicializa o i18n antes de montar o React. O detector escolhe o idioma
// (localStorage → navegador → pt-BR) e mantemos o <html lang> em sincronia.
const i18n = initI18n()

// Na landing o idioma vem da URL ("/" = pt-BR, "/en" = inglês): casa com a
// página estática pré-renderizada (ver scripts/prerender.mjs) e evita
// divergência na hidratação. Fora dela vale a preferência resolvida no init.
if (isLandingPath(window.location.pathname)) {
  applyLandingLanguage(landingLang(window.location.pathname))
}
syncHtmlLang(i18n.language)
i18n.on('languageChanged', syncHtmlLang)

// Só a escolha explícita do usuário vai para o backend (usada em e-mails/push);
// a troca temporária da landing não deve alterar o perfil. Fire-and-forget:
// erros de rede aqui não devem quebrar a troca de idioma.
onLanguagePreferenceChange((lng) => {
  if (localStorage.getItem('access_token') || sessionStorage.getItem('access_token')) {
    api.put('/api/auth/profile', { locale: lng }).catch(() => {})
  }
})

// Registra o Service Worker no carregamento. Além dos lembretes push, isso
// satisfaz os critérios de "instalável" do Chrome/Edge para oferecer o app.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {})
  })
}

const app = (
  <StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </StrictMode>
)

// A landing ("/") é pré-renderizada no build (SSG) e injetada no index.html.
// Um visitante novo (sem token) que abre "/" tem exatamente a landing como
// primeiro render → hidratamos por cima do HTML estático, e o LCP pinta sem
// esperar o React montar. Nos demais casos (rota diferente ou sessão existente,
// onde o primeiro render seria o Dashboard/splash e não a landing) limpamos o
// markup pré-renderizado e renderizamos do zero no cliente.
const hasToken = !!(
  localStorage.getItem('access_token') || sessionStorage.getItem('access_token')
)

// A landing é pré-renderizada por idioma: "/" em pt-BR, "/en" em inglês. Só
// hidratamos quando o idioma ativo bate com o da página estática — senão (ex.:
// visitante em "/" cujo idioma detectado é inglês) renderizamos do zero, sem
// risco de divergência de hidratação.
const path = window.location.pathname
const prerenderLang = landingLang(path)
const canHydrateLanding =
  isLandingPath(path) &&
  !hasToken &&
  rootEl.firstElementChild &&
  i18n.language === prerenderLang

if (canHydrateLanding) {
  hydrateRoot(rootEl, app)
} else {
  rootEl.innerHTML = ''
  createRoot(rootEl).render(app)
}
